import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Star, Package, Handshake, Bell, ChevronRight, ShieldCheck, Upload, Calendar, AlertTriangle, CreditCard, Share2, Camera, Wallet, Gift, Copy } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useUser } from '@/contexts/UserContext';
import api from '@/lib/api';
import UserBadges from '@/components/UserBadges';
import { toast } from 'sonner';

const getMediaUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${url}`;
};

export default function ProfilePage() {
  const user = useUser();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ items: 0, deals: 0 });
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showKYC, setShowKYC] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [profilePic, setProfilePic] = useState(user?.picture || '');
  const [uploadingPic, setUploadingPic] = useState(false);
  const avatarInputRef = useRef(null);

  // KYC state
  const [dob, setDob] = useState('');
  const [curp, setCurp] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const ineFrontRef = useRef(null);
  const ineBackRef = useRef(null);
  const guardianRef = useRef(null);
  const [ineFrontFile, setIneFrontFile] = useState(null);
  const [ineBackFile, setIneBackFile] = useState(null);
  const [guardianFile, setGuardianFile] = useState(null);

  // Payment state
  const [spinNumber, setSpinNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [clabe, setClabe] = useState('');
  const [paymentSaving, setPaymentSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/items/my'), api.get('/offers/my'), api.get('/notifications'), api.get('/users/payment-info')]).then(([items, offers, notifs, payment]) => {
      setStats({ items: items.data.length, deals: offers.data.filter(o => o.status === 'completed').length });
      setNotifications(notifs.data);
      const pi = payment.data;
      setSpinNumber(pi.spin_number || '');
      setBankName(pi.bank_name || '');
      setClabe(pi.clabe || '');
    }).catch(() => {});
  }, []);

  const handleLogout = async () => { try { await api.post('/auth/logout'); } catch {} navigate('/'); };
  const markRead = async () => { await api.put('/notifications/read').catch(() => {}); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPic(true);
    const fd = new FormData();
    fd.append('picture', file);
    try {
      const res = await api.put('/auth/picture', fd);
      setProfilePic(res.data.picture);
      if (user?.refreshUser) await user.refreshUser();
      toast.success('Foto de perfil actualizada');
    } catch { toast.error('Error al subir la imagen'); }
    finally { setUploadingPic(false); }
  };
  const unreadCount = notifications.filter(n => !n.read).length;

  const checkAge = (dobStr) => {
    if (!dobStr) return;
    const birth = new Date(dobStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    setIsMinor(age < 18);
  };

  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    if (!dob || !curp) { toast.error('Fecha de nacimiento y CURP son obligatorios'); return; }
    if (!isMinor && (!ineFrontFile || !ineBackFile)) { toast.error('Sube ambos lados de tu INE'); return; }
    if (isMinor && !guardianFile) { toast.error('Sube la INE del tutor'); return; }
    setKycSubmitting(true);
    const fd = new FormData();
    fd.append('date_of_birth', dob);
    fd.append('curp', curp.toUpperCase());
    fd.append('is_minor', isMinor.toString());
    if (ineFrontFile) fd.append('ine_front', ineFrontFile);
    if (ineBackFile) fd.append('ine_back', ineBackFile);
    if (guardianFile) fd.append('guardian_ine', guardianFile);
    try {
      await api.post('/auth/verify', fd);
      toast.success('Verificacion enviada! Pendiente de revision.');
      setShowKYC(false);
    } catch { toast.error('Error al enviar verificacion'); }
    finally { setKycSubmitting(false); }
  };

  const handlePaymentSave = async () => {
    setPaymentSaving(true);
    try {
      await api.put('/users/payment-info', { spin_number: spinNumber, bank_name: bankName, clabe });
      toast.success('Datos de cobro guardados');
    } catch { toast.error('Error al guardar'); }
    finally { setPaymentSaving(false); }
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/profile/${user?.user_id || ''}`;
    const shareData = { title: `${user?.name} en Bisne`, text: `Mira mi perfil en Bisne!`, url };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success('Link de perfil copiado!'); } catch { toast.error('Error al copiar'); }
    }
  };

  const verificationStatus = user?.verification_status;

  return (
    <div data-testid="profile-page" className="min-h-screen bg-background page-with-nav">
      <div className="max-w-md mx-auto px-4">
        <h1 className="font-heading text-3xl text-primary uppercase mb-6">PERFIL</h1>

        <div className="bg-card border-2 border-border rounded-sm p-6 text-center space-y-4">
          <div className="relative w-24 h-24 mx-auto">
            <Avatar className="w-24 h-24 border-2 border-primary">
              <AvatarImage src={getMediaUrl(profilePic)} />
              <AvatarFallback className="bg-secondary text-foreground font-heading text-2xl">{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <button data-testid="profile-avatar-edit" onClick={() => avatarInputRef.current?.click()} disabled={uploadingPic}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center border-2 border-card hover:bg-primary/80 transition-colors">
              {uploadingPic
                ? <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                : <Camera className="w-4 h-4" />}
            </button>
            <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
          </div>
          <div>
            <h2 className="font-body font-bold text-foreground text-lg">{user?.name}</h2>
            <p className="text-muted-foreground text-sm font-body">{user?.email}</p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Star className="w-4 h-4 text-primary fill-primary" />
            <span className="font-heading text-lg text-primary">{user?.trust_score || '5.0'}</span>
            <span className="text-muted-foreground text-xs font-body ml-1">Trust Score</span>
            {user?.is_verified && <ShieldCheck className="w-4 h-4 text-green-500 ml-2" />}
            <UserBadges user={user || {}} size="sm" />
          </div>
          {/* Share profile button */}
          <Button data-testid="profile-share-btn" onClick={handleShareProfile} variant="outline" size="sm"
            className="rounded-sm font-body text-xs border-border text-foreground">
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartir mi Perfil
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-card border-2 border-border rounded-sm p-4 text-center">
            <Package className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="font-heading text-2xl text-foreground">{stats.items}</p>
            <p className="text-muted-foreground text-xs font-body">Articulos</p>
          </div>
          <div className="bg-card border-2 border-border rounded-sm p-4 text-center">
            <Handshake className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="font-heading text-2xl text-foreground">{stats.deals}</p>
            <p className="text-muted-foreground text-xs font-body">Tratos</p>
          </div>
        </div>

        {/* Cartera / Wallet */}
        <div data-testid="profile-wallet" className="mt-4 bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-body text-xs text-zinc-500 uppercase tracking-wider">Cartera</p>
                <p className="font-heading text-2xl text-primary leading-none mt-0.5">${user?.bisne_cash || 0}</p>
              </div>
            </div>
            <span className="font-body text-xs text-zinc-500 bg-zinc-800 px-3 py-1 rounded-full">Bisne Cash</span>
          </div>
        </div>

        {/* Invita y Gana */}
        <div data-testid="profile-referral" className="mt-4 bg-zinc-900 border border-primary/20 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Gift className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-heading text-base text-foreground">Invita y gana</p>
              <p className="text-zinc-500 font-body text-xs">Ambos ganan $20 Bisne Cash</p>
            </div>
          </div>
          {user?.referral_code && (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5">
                <p className="text-zinc-500 font-body text-[10px] uppercase tracking-wider">Tu codigo</p>
                <p data-testid="profile-referral-code" className="font-heading text-lg text-primary">{user.referral_code}</p>
              </div>
              <Button data-testid="profile-copy-referral" variant="outline" className="h-full rounded-xl border-zinc-700 px-4"
                onClick={() => {
                  const shareUrl = `${window.location.origin}/login?ref=${user.referral_code}`;
                  navigator.clipboard.writeText(shareUrl).then(() => toast.success('Link de referido copiado!')).catch(() => {
                    navigator.clipboard.writeText(user.referral_code).then(() => toast.success('Codigo copiado!'));
                  });
                }}>
                <Copy className="w-5 h-5 text-primary" />
              </Button>
            </div>
          )}
          <p className="text-zinc-500 font-body text-xs leading-relaxed">Comparte tu codigo con un amigo. Cuando se registre, ambos ganan $20 Bisne Cash.</p>
        </div>

        {/* Payment Info Section */}
        <div className="mt-4">
          <button data-testid="profile-payment-btn" onClick={() => setShowPayment(!showPayment)}
            className="w-full bg-card border-2 border-border rounded-sm p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-primary" />
              <span className="font-body font-medium text-foreground">Datos de Cobro</span>
            </div>
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showPayment ? 'rotate-90' : ''}`} />
          </button>
          {showPayment && (
            <div className="bg-card border-2 border-t-0 border-border rounded-b-sm p-4 space-y-3">
              <div className="space-y-2">
                <label className="font-body text-sm text-foreground">Numero SPIN</label>
                <Input data-testid="payment-spin" value={spinNumber} onChange={e => setSpinNumber(e.target.value)}
                  placeholder="Tu numero de SPIN" className="rounded-sm border-2 bg-background font-body h-11" />
              </div>
              <div className="space-y-2">
                <label className="font-body text-sm text-foreground">Banco</label>
                <Input data-testid="payment-bank" value={bankName} onChange={e => setBankName(e.target.value)}
                  placeholder="Ej: BBVA, Banorte" className="rounded-sm border-2 bg-background font-body h-11" />
              </div>
              <div className="space-y-2">
                <label className="font-body text-sm text-foreground">CLABE Interbancaria</label>
                <Input data-testid="payment-clabe" value={clabe} onChange={e => setClabe(e.target.value)}
                  placeholder="18 digitos" maxLength={18} className="rounded-sm border-2 bg-background font-body h-11" />
              </div>
              <Button data-testid="payment-save-btn" onClick={handlePaymentSave} disabled={paymentSaving}
                className="w-full h-11 rounded-sm font-body font-bold uppercase text-sm bg-primary text-primary-foreground">
                {paymentSaving ? 'GUARDANDO...' : 'GUARDAR DATOS'}
              </Button>
            </div>
          )}
        </div>

        {/* KYC Section */}
        <div className="mt-4">
          <button data-testid="profile-kyc-btn" onClick={() => setShowKYC(!showKYC)}
            className="w-full bg-card border-2 border-border rounded-sm p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <span className="font-body font-medium text-foreground">Verificar Identidad</span>
            </div>
            <div className="flex items-center gap-2">
              {verificationStatus === 'pending' && <Badge className="rounded-sm text-[9px] bg-yellow-600/80 text-white border-0">Pendiente</Badge>}
              {user?.is_verified && <Badge className="rounded-sm text-[9px] bg-green-600/80 text-white border-0">Verificado</Badge>}
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showKYC ? 'rotate-90' : ''}`} />
            </div>
          </button>

          {showKYC && (
            <form onSubmit={handleKYCSubmit} className="bg-card border-2 border-t-0 border-border rounded-b-sm p-4 space-y-4">
              <div className="space-y-2">
                <label className="font-body text-sm text-foreground flex items-center gap-2"><Calendar className="w-4 h-4" /> Fecha de Nacimiento</label>
                <Input data-testid="kyc-dob" type="date" value={dob} onChange={e => { setDob(e.target.value); checkAge(e.target.value); }}
                  className="rounded-sm border-2 bg-background font-body h-11" />
              </div>

              {isMinor && (
                <div className="bg-orange-500/10 border-2 border-orange-500/30 rounded-sm p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm text-orange-400 font-bold">Autorizacion de Tutor Requerida</p>
                    <p className="font-body text-xs text-orange-300/80">Eres menor de edad. Se necesita la INE de tu tutor.</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="font-body text-sm text-foreground">CURP</label>
                <Input data-testid="kyc-curp" value={curp} onChange={e => setCurp(e.target.value)} placeholder="18 caracteres" maxLength={18}
                  className="rounded-sm border-2 bg-background font-body h-11 uppercase" />
              </div>

              {!isMinor ? (
                <>
                  <div className="space-y-2">
                    <label className="font-body text-sm text-foreground">INE Frente</label>
                    <button type="button" data-testid="kyc-ine-front-btn" onClick={() => ineFrontRef.current?.click()}
                      className={`w-full h-14 rounded-sm border-2 border-dashed flex items-center justify-center gap-2 font-body text-sm transition-colors ${ineFrontFile ? 'border-green-500 text-green-400' : 'border-border text-muted-foreground hover:border-primary'}`}>
                      <Upload className="w-4 h-4" /> {ineFrontFile ? ineFrontFile.name : 'Subir INE Frente'}
                    </button>
                    <input ref={ineFrontRef} type="file" accept="image/*" onChange={e => setIneFrontFile(e.target.files?.[0])} className="hidden" />
                  </div>
                  <div className="space-y-2">
                    <label className="font-body text-sm text-foreground">INE Reverso</label>
                    <button type="button" data-testid="kyc-ine-back-btn" onClick={() => ineBackRef.current?.click()}
                      className={`w-full h-14 rounded-sm border-2 border-dashed flex items-center justify-center gap-2 font-body text-sm transition-colors ${ineBackFile ? 'border-green-500 text-green-400' : 'border-border text-muted-foreground hover:border-primary'}`}>
                      <Upload className="w-4 h-4" /> {ineBackFile ? ineBackFile.name : 'Subir INE Reverso'}
                    </button>
                    <input ref={ineBackRef} type="file" accept="image/*" onChange={e => setIneBackFile(e.target.files?.[0])} className="hidden" />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <label className="font-body text-sm text-foreground">INE del Tutor</label>
                  <button type="button" data-testid="kyc-guardian-btn" onClick={() => guardianRef.current?.click()}
                    className={`w-full h-14 rounded-sm border-2 border-dashed flex items-center justify-center gap-2 font-body text-sm transition-colors ${guardianFile ? 'border-green-500 text-green-400' : 'border-border text-muted-foreground hover:border-primary'}`}>
                    <Upload className="w-4 h-4" /> {guardianFile ? guardianFile.name : 'Subir INE del Tutor'}
                  </button>
                  <input ref={guardianRef} type="file" accept="image/*" onChange={e => setGuardianFile(e.target.files?.[0])} className="hidden" />
                </div>
              )}

              <Button data-testid="kyc-submit-btn" type="submit" disabled={kycSubmitting}
                className="w-full h-12 rounded-sm font-body font-bold uppercase bg-primary text-primary-foreground">
                {kycSubmitting ? 'ENVIANDO...' : 'ENVIAR VERIFICACION'}
              </Button>
            </form>
          )}
        </div>

        {/* Notifications */}
        <button data-testid="profile-notifications-btn" onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs && unreadCount > 0) markRead(); }}
          className="w-full mt-4 bg-card border-2 border-border rounded-sm p-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><Bell className="w-5 h-5 text-primary" /><span className="font-body font-medium text-foreground">Notificaciones</span></div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && <span className="bg-destructive text-white text-[10px] px-2 py-0.5 font-bold rounded-sm">{unreadCount}</span>}
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showNotifs ? 'rotate-90' : ''}`} />
          </div>
        </button>

        {showNotifs && (
          <div className="bg-card border-2 border-t-0 border-border rounded-b-sm max-h-64 overflow-y-auto scrollbar-hide">
            {notifications.length === 0 ? <p className="p-4 text-muted-foreground text-sm font-body text-center">Sin notificaciones</p>
            : notifications.map(n => (
              <div key={n.notification_id} data-testid={`notif-${n.notification_id}`} onClick={() => n.offer_id && navigate('/deals')}
                className={`p-3 border-b border-border last:border-0 cursor-pointer hover:bg-accent/50 ${!n.read ? 'bg-primary/5' : ''}`}>
                <p className="font-body text-sm text-foreground">{n.content}</p>
                <p className="text-muted-foreground text-[10px] font-body mt-1">{new Date(n.created_at).toLocaleDateString('es-MX')}</p>
              </div>
            ))}
          </div>
        )}

        <Separator className="my-6" />

        <div className="flex items-center justify-center gap-4 mb-4">
          <Link to="/terminos" data-testid="profile-terms-link" className="text-muted-foreground text-xs font-body underline underline-offset-2 hover:text-primary transition-colors">Terminos y Condiciones</Link>
          <span className="text-zinc-600">|</span>
          <Link to="/privacidad" data-testid="profile-privacy-link" className="text-muted-foreground text-xs font-body underline underline-offset-2 hover:text-primary transition-colors">Aviso de Privacidad</Link>
        </div>

        <Button data-testid="logout-btn" onClick={handleLogout} variant="outline"
          className="w-full rounded-sm font-body font-bold uppercase border-destructive text-destructive hover:bg-destructive hover:text-white h-12">
          <LogOut className="w-4 h-4 mr-2" /> CERRAR SESION
        </Button>
      </div>
    </div>
  );
}
